import React from 'react';
import { TrendingDown, Users, LifeBuoy, Activity } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../Card';
import { formatPercent, topSection } from '../../utils/business';
import type { BucketRow } from '../../types/traffic';
import type { TrafficSummary } from '../../utils/traffic';
interface ExecutiveSummaryProps {
  summary: TrafficSummary;
  sections: BucketRow[];
}

/** Measured outage context: where people were headed, and how they behaved. */
export function ExecutiveSummary({
  summary,
  sections
}: ExecutiveSummaryProps) {
  const top = topSection(sections);
  return <section aria-label="Outage context" className="grid gap-4 sm:gap-6 lg:grid-cols-3" data-id="element-42" data-mp="cizi5ptwfp1v">
      <Card size="sm" data-id="element-43" data-mp="7k6867dtmg6g">
        <CardHeader data-id="element-44" data-mp="7ky27drrs5um">
          <CardTitle className="flex items-center gap-2 text-base" data-id="element-45" data-mp="7nk693fgc383">
            <Users className="h-4 w-4 text-muted-foreground" aria-hidden="true" data-id="element-46" data-mp="2xc30jef05q3l" />
            Hardest-hit area
          </CardTitle>
          <CardDescription data-id="element-47" data-mp="2sdrnyvxbp1ho">Where impacted users were trying to go</CardDescription>
        </CardHeader>
        <CardContent data-id="element-48" data-mp="2yj2eydbsa458">
          <p className="font-mono text-lg text-foreground" data-id="element-49" data-mp="2yjly1xd66yku">{top?.label ?? '—'}</p>
          <p className="mt-1 text-xs text-muted-foreground" data-id="element-50" data-mp="2jm1yx72yfxml">
            {formatPercent(top?.share ?? 0)} of all blocked requests
          </p>
        </CardContent>
      </Card>

      <Card size="sm" data-id="element-51" data-mp="2omzc1593y1h8">
        <CardHeader data-id="element-52" data-mp="2atrlzi0ode1n">
          <CardTitle className="flex items-center gap-2 text-base" data-id="element-53" data-mp="2at81punjydkg">
            <TrendingDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" data-id="element-54" data-mp="2fk80z6k597q4" />
            Gave up immediately
          </CardTitle>
          <CardDescription data-id="element-55" data-mp="20wmynev3fcj9">Left under 10s without retrying</CardDescription>
        </CardHeader>
        <CardContent data-id="element-56" data-mp="25vgcj8eal5jy">
          <p className="font-mono text-lg text-foreground" data-id="element-57" data-mp="25uohbyolx9u7">{formatPercent(summary.bounceRate)}</p>
          <p className="mt-1 text-xs text-muted-foreground" data-id="element-58" data-mp="1rvfd7xdoe66l">
            {formatPercent(summary.returningShare)} came back to check again later
          </p>
        </CardContent>
      </Card>

      <Card size="sm" data-id="element-59" data-mp="2f30gpx4oyf6">
        <CardHeader data-id="element-60" data-mp="2fl2622k84uh">
          <CardTitle className="flex items-center gap-2 text-base" data-id="element-61" data-mp="3tsfsqg5ay6mc">
            <Activity className="h-4 w-4 text-muted-foreground" aria-hidden="true" data-id="element-62" data-mp="3tq31tdhkglx5" />
            Peak concurrency
          </CardTitle>
          <CardDescription data-id="element-63" data-mp="3ju9ss4323ak4">Most users waiting at once</CardDescription>
        </CardHeader>
        <CardContent data-id="element-64" data-mp="lccneh1nwu16">
          <p className="font-mono text-lg text-foreground" data-id="element-65" data-mp="lcmbu0726t4r">
            {summary.peakConcurrency.toLocaleString()}
          </p>
          <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground" data-id="element-66" data-mp="gdsyc7rrv24l">
            <LifeBuoy className="h-3 w-3" aria-hidden="true" data-id="element-67" data-mp="bcvfru2uo62k" />
            {summary.supportEmailClicks.toLocaleString()} support contacts so far
          </p>
        </CardContent>
      </Card>
    </section>;
}